import { buildHeadersWithWidgetToken } from '@elfsight/lib-utils';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import { API_URL, IS_DEVELOPMENT } from '../../constants';
import type { PollSettings, VotesData } from '../../types';
import { useFakeInitialData } from '../local-data';

type FetchDataResponse = {
  code: number;
  message: string;
  payload: VotesData;
};

export function useVotesQuery(
  queryKey: QueryKey,
  answers: PollSettings['answers']
) {
  const { editMode, widgetToken } = useSettings();
  const queryClient = useQueryClient();
  const { applyFakeData, fakeInitialData } = useFakeInitialData(answers);

  const editModeSettings: Omit<UseQueryOptions<VotesData>, 'queryKey'> = {
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    ...(IS_DEVELOPMENT && !widgetToken
      ? { placeholderData: fakeInitialData }
      : {})
  };

  const query = useQuery<VotesData, Error, VotesData>({
    queryKey,
    queryFn: async () => {
      if (applyFakeData) {
        return fakeInitialData;
      }

      const params = new URLSearchParams();
      answers.forEach(({ id }) => params.append('votes[]', id));

      const headers = buildHeadersWithWidgetToken(widgetToken, {
        'content-type': 'application/json'
      });

      const response = await fetch(`${API_URL}/api/poll?${params.toString()}`, {
        method: 'GET',
        headers
      });

      const { code, message, payload }: FetchDataResponse =
        await response.json();

      if (code === 401) {
        throw new Error(message);
      }

      return payload;
    },
    enabled: !!widgetToken,
    ...(editMode ? editModeSettings : {})
  });

  function setData(data: Partial<VotesData>) {
    queryClient.setQueryData(queryKey, data);
  }

  return { ...query, setData };
}
